import { Component, OnInit, OnChanges, Input } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthenticationService, AlertService } from '@app/_services';
import { OwnerReviewService } from '../../owner-review.service';
import { ConfirmationService, MessageService } from 'primeng/api';
import { first } from 'rxjs/operators';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { PropertyService } from '@app/_components/property/property.service';
import * as moment from 'moment';
import { ConstantService } from '@app/_services/constant.service';
import { Reservation, ReservationService } from '@app/_components/reservation';
import { ActivityService } from '@app/_components/activity';

@Component({
    selector: 'app-owner-review',
    templateUrl: './owner-review.component.html',
    styleUrls: ['./owner-review.component.css'],
    standalone: false
})
export class OwnerReviewComponent implements OnInit, OnChanges {

	form1: UntypedFormGroup;
	formLoading = false;
	formErrorMessage: string;
	properties: any;
	showReservationSearch = false;

	ownerReview: any;

	validationMessages =
		{
			ownerReviewId: [{ type: 'required', message: 'Required' }],
			createDate: [{ type: 'required', message: 'Required' }],
			lastUpdate: [{ type: 'required', message: 'Required' }],
			checkInDate: [],
			reviewDate: [{ type: 'required', message: 'Required' }],
			rating: [{ type: 'required', message: 'Required' }],
			ratingScale: [{ type: 'required', message: 'Required' }],

			// 1 approved / 2 suspended / 3 incomplete
			status: [{ type: 'required', message: 'Required' }],
			title: [{ type: 'required', message: 'Required' }],
			reviewDesc: [{ type: 'required', message: 'Required' }, { type: 'minlength', message: 'Minimum length is 50 characters' }],
			responseDesc: [{ type: 'required', message: 'Required' }],
			helpfulCount: [{ type: 'required', message: 'Required' }],
			notHelpfulCount: [{ type: 'required', message: 'Required' }],

			// ownerReviewer
			firstName: [{ type: 'required', message: 'Required' }],
			lastName: [{ type: 'required', message: 'Required' }],
			emailAddress: [{ type: 'required', message: 'Required' }],
			screenName: [{ type: 'required', message: 'Required' }],
			city: [{ type: 'required', message: 'Required' }],
			state: [{ type: 'required', message: 'Required' }],
			country: [{ type: 'required', message: 'Required' }],

			// links
			propertyCode: [{ type: 'required', message: 'Required' }],
			shortTermRentalId: [{ type: 'required', message: 'Required' }],
		};



	statuses = [
		{ name: 'Draft', value: 1 },
		{ name: 'New', value: 2 },
		{ name: 'Approved', value: 3 },
		{ name: 'Rejected', value: 4 },
	];
    activities: any;
    countries: any;
    states: any[];



	/*
	*
	*
	*/
	constructor(
		private reservationService: ReservationService,
		private activityService: ActivityService,
		private formBuilder: UntypedFormBuilder,
		private ownerReviewService: OwnerReviewService,
		private messageService: MessageService,
		private dynamicDialogRef: DynamicDialogRef,
		private dynamicDialogConfig: DynamicDialogConfig,
		private propertyService: PropertyService,
		private constants: ConstantService,
		private confirmationService: ConfirmationService
	) {
		this.countries = this.constants.countries; 
		this.states = this.constants.states;
		}

	ngOnInit(): void {
		this.loadProperties();
		this.loadActivities();
		this.buildForm();
		if (this.dynamicDialogConfig.data) {

			// get the ownerReview
			this.ownerReview = this.dynamicDialogConfig.data.ownerReview;

			// create dates for primeng
			this.fixDates();

			// patch to form
			this.form1.patchValue(this.dynamicDialogConfig.data.ownerReview);
		}
	}

	onFindReservationClick() {
		this.showReservationSearch = !this.showReservationSearch;
	}

	onAddClick() {
		this.addOwnerReview();
	}

	onUpdateClick() {
		this.updateOwnerReview();
	}

	onDeleteClick() {
		this.confirmationService.confirm({
			message: 'Are you sure you want to delete this ownerReview?',
			accept: () => {
				this.ownerReview.action = 'DELETED'; // to tell caller what we did
				this.deleteOwnerReview();
				this.dynamicDialogRef.close(this.ownerReview);
			}
		});
	}

	ngOnChanges() {

	}

	onCloseClick() {
		// pass the ownerReview back to the ownerReview-list component to update the list.
		this.dynamicDialogRef.close(this.ownerReview);
	}

	findOwnerReviewById(ownerReviewId: number) {
		this.formLoading = true;
		this.ownerReviewService.findById(ownerReviewId).pipe(first()).subscribe(
			res => {
				this.ownerReview = res.data.ownerReview;

				// create dates for primeng
				this.fixDates();

				this.form1.patchValue(this.ownerReview);
			},
			err => {
				console.log('error In findOwnerReviewById is: ' + JSON.stringify(err));
				this.formLoading = false;
				this.messageService.add({ severity: 'error', summary: 'Error', detail: 'We were unable to retrieve your ownerReview: ' + err.message, sticky: true });
			},
			() => { this.formLoading = false; });
	}



	addOwnerReview() {
		this.customValidate();
		this.form1.markAllAsTouched();

		// stop here if form is invalid
		if (this.form1.invalid) {
			return;
		}

		this.formLoading = true;
		this.ownerReviewService.add(this.form1.value)
			.pipe(first()).subscribe(
				res => {
					this.ownerReview = res.data.ownerReview;

					// create dates for primeng
					this.fixDates();
					this.form1.patchValue(this.ownerReview);
					this.ownerReview.action = 'ADDED'; // to tell caller what we did
					this.messageService.add({ severity: 'success', summary: 'Success!', detail: 'New ownerReview added.', sticky: false });
				},
				err => {
					// Do NOT reset formLoading on error — a 504 means the owner review may have been created.
					// Keep button disabled to prevent a duplicate review from being submitted.
					console.log('error in ownerReviewService.add: ' + JSON.stringify(err));
					this.formErrorMessage = err.message;
					this.messageService.add({ severity: 'error', summary: 'Create Failed', detail: 'Owner review save may have completed. Please refresh before retrying.', sticky: true });
				},
				() => { this.formLoading = false; });
	}

	updateOwnerReview() {
		this.customValidate();
		this.form1.markAllAsTouched();

		// stop here if form is invalid
		if (this.form1.invalid) {
			return;
		}

		this.formLoading = true;
		this.ownerReviewService.update(this.form1.value)
			.pipe(first()).subscribe(
				res => {
					this.ownerReview = res.data.ownerReview;
					// create dates for primeng
					this.fixDates();
					this.form1.patchValue(this.ownerReview);
					this.ownerReview.action = 'UPDATED'; // to tell caller what we did
					this.messageService.add({ severity: 'success', summary: 'Update Successfull', detail: 'Settings updated.', sticky: false });

				},
				err => {
					// Do NOT reset formLoading on error — a 504 means the update may have persisted.
					// Keep button disabled to block re-submit.
					console.log('error in ownerReviewService.update: ' + JSON.stringify(err));
					this.formErrorMessage = err.message;
					this.messageService.add({ severity: 'error', summary: 'Update Failed', detail: 'Owner review update may have completed. Please refresh before retrying.', sticky: true });
				},
				() => { this.formLoading = false; });
	}


	deleteOwnerReview() {
		this.customValidate();
		this.formLoading = true;
		this.ownerReviewService.delete(this.ownerReview.ownerReviewId)
			.pipe(first()).subscribe(
				res => {

				},
				err => {
					console.log('error in ownerReviewService.add: ' + JSON.stringify(err));
					this.formLoading = false;
					this.formErrorMessage = err.message;
					this.messageService.add({ severity: 'error', summary: 'Create Failed', detail: 'We were unable to add the ownerReview: ' + err.message, sticky: true });
				},
				() => { this.formLoading = false; });
	}

	buildForm() {
		this.form1 = this.formBuilder.group({
			ownerReviewId: [''],
			createDate: [''],
			lastUpdate: [''],
			checkInDate: [''],
			reviewDate: ['', Validators.required],
			rating: ['', Validators.required],
			ratingScale: [''],

			// 1 approved / 2 suspended / 3 incomplete
			status: [1, Validators.required],
			title: [''],
			reviewDesc: [''], //, Validators.compose([Validators.minLength(50), Validators.required])
			responseDesc: [''],
			helpfulCount: [''],
			notHelpfulCount: [''],

			// ownerReviewer
			firstName: ['', Validators.required],
			lastName: ['', Validators.required],
			emailAddress: [''],
			screenName: [''],
			city: [''],
			state: [''],
			country: [''],

			// links
			shortTermRentalId: ['', Validators.required],
		});
	}

	loadProperties() {
		if (!this.properties || this.properties === undefined || this.properties.length < 1) {
			this.propertyService.findAllSummary()
				.subscribe(
					res => {
						// console.log('findAllPropertiesSummary: ' + JSON.stringify(res));
						this.properties = res.data.properties;
						return this.properties;
					},
					err => {
						console.log('Feature-Update FAIL: ' + JSON.stringify(err));
						this.messageService.add({ severity: 'error', summary: 'Create Failed', detail: 'We were unable to add the ownerReview: ' + err.message, sticky: true });
					},
					() => { });
		}
	}
	
	loadActivities() {
		if (!this.activities || this.activities === undefined || this.activities.length < 1) {
			this.activityService.findAll()
				.subscribe(
					res => {
						console.log('findAllactivities: ' + JSON.stringify(res));
						this.activities = res.data.activitys;
						return this.activities;
					},
					err => {
						console.log('Feature-Update FAIL: ' + JSON.stringify(err));
						this.messageService.add({ severity: 'error', summary: 'Create Failed', detail: 'We were unable to add the ownerReview: ' + err.message, sticky: true });
					},
					() => { });
		}
	}

	fixDates() {
		// create date objects - why can't primeng do this?
		this.ownerReview.reviewDate = moment(this.ownerReview.reviewDate).toDate();
	}

	customValidate() {
		// if NEW or APPROVED
		if (this.form1.get('status').value === 2 || this.form1.get('status').value === 3) {
			this.form1.get('reviewDesc').setValidators([Validators.minLength(50), Validators.required]);
			this.form1.get('reviewDesc').updateValueAndValidity();
			this.form1.get('title').setValidators([Validators.required]);
			this.form1.get('title').updateValueAndValidity();
		}
		else {
			this.form1.get('reviewDesc').clearValidators();
			this.form1.get('title').clearValidators();
		}
	}

}
